import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, TrendingUp, Link2, FileText, Settings, BarChart3, 
  Plus, Globe, Target, CheckCircle2, AlertCircle, Clock, ArrowRight,
  ArrowLeft, RefreshCw, ExternalLink, Trash2, Eye, Download, Play,
  ChevronRight, Zap, Shield, Star, FileSearch, Calendar, 
  CheckCheck, X, Info, Lock, Users, LineChart, PieChart,
  MousePointer, Smartphone, Monitor, MapPin
} from 'lucide-react';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ==================== PROJECT LIST VIEW ====================
const ProjectListView = ({ onSelectProject, onCreateProject, onTakeTour }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.getDFYProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (searchQuery && !p.project_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const statusCounts = {
    all: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    paused: projects.filter(p => p.status === 'paused').length,
    trial: projects.filter(p => p.status === 'trial').length,
    cancelled: projects.filter(p => p.status === 'cancelled').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Your Projects</h1>
          <p className="text-gray-400">Manage your SEO & PPC projects here.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onTakeTour} data-testid="take-tour-btn">
            <Play className="w-4 h-4 mr-2" />
            Take a Tour
          </Button>
          <Button onClick={onCreateProject} data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Project
          </Button>
          <Button variant="ghost" size="icon">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'active', 'paused', 'trial', 'cancelled'].map(status => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilter(status)}
            className="capitalize"
          >
            {status} ({statusCounts[status]})
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Projects Table */}
      <Card className="bg-card/50 border-white/10">
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium">Project Name</th>
                <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-gray-400">
                    Add your first project for free on the &apos;Add Project&apos; button above.
                  </td>
                </tr>
              ) : (
                filteredProjects.map(project => (
                  <tr 
                    key={project.id} 
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => onSelectProject(project)}
                    data-testid={`project-row-${project.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          project.service_type === 'seo' ? "bg-cyan-500/20" : "bg-purple-500/20"
                        )}>
                          {project.service_type === 'seo' ? (
                            <TrendingUp className="w-5 h-5 text-cyan-400" />
                          ) : (
                            <Target className="w-5 h-5 text-purple-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{project.project_name}</p>
                          <p className="text-gray-400 text-sm">{project.website_url}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={project.status === 'active' ? 'default' : project.status === 'trial' ? 'outline' : 'secondary'}>
                        {project.status === 'trial' && '🎁 '}{project.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-gray-400">
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== CREATE PROJECT WIZARD ====================
const CreateProjectWizard = ({ onBack, onCreated }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [formData, setFormData] = useState({
    project_name: '',
    website_url: '',
    service_type: 'seo'
  });

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.getDFYPricing();
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.project_name || !formData.website_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await api.createDFYProject(formData);
      toast.success('Project created! Your 7-day free trial has started.');
      onCreated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <button onClick={onBack} className="hover:text-white">Dashboard</button>
        <ChevronRight className="w-4 h-4" />
        <button onClick={onBack} className="hover:text-white">Projects</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">Add Project</span>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
            step >= 1 ? "bg-white text-black" : "bg-white/20 text-white"
          )}>1</div>
          <span className="text-white">Project Details</span>
        </div>
        <div className="w-12 h-px bg-white/20" />
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
            step >= 2 ? "bg-white text-black" : "bg-white/20 text-white"
          )}>2</div>
          <span className={step >= 2 ? "text-white" : "text-gray-400"}>Free Trial</span>
        </div>
      </div>

      {/* Step 1: Project Details */}
      {step === 1 && (
        <Card className="bg-card/80 border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">Let&apos;s get started</CardTitle>
            <CardDescription className="text-base">
              Every project comes with a 7 day free trial so that we can set everything up for you
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-white">Project Name</Label>
              <Input
                placeholder="My Awesome Website"
                value={formData.project_name}
                onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                data-testid="project-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Website URL</Label>
              <div className="flex">
                <span className="flex items-center px-3 bg-white/10 border border-r-0 border-white/20 rounded-l-md text-gray-400">
                  https://
                </span>
                <Input
                  placeholder="example.com"
                  value={formData.website_url.replace('https://', '')}
                  onChange={(e) => setFormData({...formData, website_url: `https://${e.target.value.replace('https://', '')}`})}
                  className="rounded-l-none"
                  data-testid="website-url-input"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-white">Select your service</Label>
              <p className="text-sm text-gray-400">Choose the service that best fits your needs</p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* SEO Option */}
                <div
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    formData.service_type === 'seo' 
                      ? "border-primary bg-primary/10" 
                      : "border-white/10 hover:border-white/30"
                  )}
                  onClick={() => setFormData({...formData, service_type: 'seo'})}
                  data-testid="service-seo"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2",
                      formData.service_type === 'seo' ? "border-primary bg-primary" : "border-white/30"
                    )} />
                    <span className="text-white font-semibold">SEO</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Rank on both traditional & AI search engines easily with our ethical, Google approved methods.
                  </p>
                  {pricing && (
                    <p className="mt-2 text-sm text-cyan-400">${pricing.seo_price}/month after trial</p>
                  )}
                </div>

                {/* PPC Option */}
                <div
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    formData.service_type === 'ppc' 
                      ? "border-primary bg-primary/10" 
                      : "border-white/10 hover:border-white/30"
                  )}
                  onClick={() => setFormData({...formData, service_type: 'ppc'})}
                  data-testid="service-ppc"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2",
                      formData.service_type === 'ppc' ? "border-primary bg-primary" : "border-white/30"
                    )} />
                    <span className="text-white font-semibold">PPC</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Advertise on Google&apos;s network with everything managed by us. One simple fee, everything included.
                  </p>
                  {pricing && (
                    <p className="mt-2 text-sm text-purple-400">${pricing.ppc_price}/month after trial</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button onClick={() => setStep(2)} data-testid="continue-btn">
              Continue with {formData.service_type.toUpperCase()}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 2: Free Trial Confirmation */}
      {step === 2 && (
        <Card className="bg-card/80 border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">Start Your Free Trial</CardTitle>
            <CardDescription className="text-base">
              No credit card required. Cancel anytime during your trial.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 p-6 rounded-lg border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">{formData.project_name || 'Your Project'}</h3>
                  <p className="text-gray-400">{formData.website_url || 'example.com'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-sm">Service</p>
                  <p className="text-white font-medium">{formData.service_type.toUpperCase()}</p>
                </div>
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-sm">Free Trial</p>
                  <p className="text-green-400 font-medium">7 Days</p>
                </div>
              </div>

              {pricing && (
                <div className="p-3 bg-white/5 rounded-lg text-center">
                  <p className="text-gray-400 text-sm">After Trial</p>
                  <p className="text-white font-bold text-2xl">
                    ${pricing[`${formData.service_type}_price`]}<span className="text-sm font-normal text-gray-400">/month</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Based on your {pricing.plan} plan</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <h4 className="text-white font-medium">What&apos;s included:</h4>
              <ul className="space-y-2">
                {pricing?.features[formData.service_type]?.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button className="flex-1" onClick={handleSubmit} disabled={loading} data-testid="start-trial-btn">
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Start Free Trial
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

// ==================== PROJECT DETAIL VIEW ====================
const ProjectDetailView = ({ project, isDemo = false, onBack, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [tourStep, setTourStep] = useState(isDemo ? 0 : -1);
  
  const tabs = [
    { id: 'onboarding', label: 'Onboarding', icon: CheckCheck },
    { id: 'rankings', label: 'Rankings', icon: TrendingUp },
    { id: 'audits', label: 'Audits', icon: FileSearch },
    { id: 'backlinks', label: 'Backlinks', icon: Link2 },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'search_console', label: 'Search Console', icon: LineChart },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const tourSteps = [
    { tab: 'onboarding', title: "Welcome! Let's Get Started", description: "This is where you'll set up your project details and complete the onboarding checklist for your project." },
    { tab: 'rankings', title: "Keyword Rankings", description: "Track how your target keywords rank in search results. Monitor daily changes with desktop, mobile, maps and AI mode options. Fully customizable." },
    { tab: 'audits', title: "Website Auditor", description: "Scan your website for technical SEO issues. We handle the fixes for you proactively when we get access to your website." },
    { tab: 'backlinks', title: "Backlink Monitoring", description: "Keep an eye on all backlinks pointing to your site. Discover new links, track lost ones, and monitor your domain authority growth." },
    { tab: 'content', title: "Content Hub", description: "Approve or provide feedback on our weekly blog content and web copy here. It also tracks when anything is published. Every document is hosted on Google Docs." },
    { tab: 'search_console', title: "Search Console", description: "Connect your Google Search Console to see real search performance data, including impressions, clicks, and CTR." },
    { tab: 'reports', title: "Report Hub", description: "Generate comprehensive reports to track your progress over time. We handle report setup for you, but you do also have the option to make them yourself." },
    { tab: 'settings', title: "Project Settings", description: "Configure your project preferences, manage team access and billing. Certain areas here are turned off for the product tour." }
  ];

  const handleNextTour = () => {
    if (tourStep < tourSteps.length - 1) {
      const nextStep = tourStep + 1;
      setTourStep(nextStep);
      setActiveTab(tourSteps[nextStep].tab);
    } else {
      setTourStep(-1); // End tour
    }
  };

  const handlePrevTour = () => {
    if (tourStep > 0) {
      const prevStep = tourStep - 1;
      setTourStep(prevStep);
      setActiveTab(tourSteps[prevStep].tab);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <button onClick={onBack} className="hover:text-white">Dashboard</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={onBack} className="hover:text-white">Projects</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{project.project_name}</span>
        </div>
        {isDemo && (
          <Badge variant="outline" className="text-amber-400 border-amber-400">
            <Lock className="w-3 h-3 mr-1" />
            View Only
          </Badge>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tourStep >= 0) {
                  const idx = tourSteps.findIndex(t => t.tab === tab.id);
                  if (idx >= 0) setTourStep(idx);
                }
              }}
              className={cn(
                "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                activeTab === tab.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {isDemo && (
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Exit Demo
          </Button>
        )}
      </div>

      {/* Tab Content + Tour Guide */}
      <div className="flex gap-6">
        <div className="flex-1">
          {activeTab === 'onboarding' && <OnboardingTab project={project} isDemo={isDemo} />}
          {activeTab === 'rankings' && <RankingsTab project={project} isDemo={isDemo} />}
          {activeTab === 'audits' && <AuditsTab project={project} isDemo={isDemo} />}
          {activeTab === 'backlinks' && <BacklinksTab project={project} isDemo={isDemo} />}
          {activeTab === 'content' && <ContentTab project={project} isDemo={isDemo} />}
          {activeTab === 'search_console' && <SearchConsoleTab project={project} isDemo={isDemo} />}
          {activeTab === 'reports' && <ReportsTab project={project} isDemo={isDemo} />}
          {activeTab === 'settings' && <SettingsTab project={project} isDemo={isDemo} />}
        </div>

        {/* Tour Guide Sidebar */}
        {tourStep >= 0 && (
          <div className="w-72 shrink-0">
            <Card className="bg-card/80 border-white/10 sticky top-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">{tourSteps[tourStep].title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">{tourSteps[tourStep].description}</p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handlePrevTour}
                  disabled={tourStep === 0}
                >
                  Previous
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleNextTour}
                >
                  {tourStep === tourSteps.length - 1 ? 'Finish Tour' : 'Next'}
                </Button>
              </CardFooter>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ONBOARDING TAB ====================
const OnboardingTab = ({ project, isDemo }) => {
  const onboarding = project.onboarding || {};
  const progress = onboarding.progress || 0;
  
  const checklistItems = [
    {
      key: 'keywords',
      title: 'Add Your Target Keywords',
      description: onboarding.keywords_count > 0 
        ? `✓ ${onboarding.keywords_count} keywords added` 
        : 'Add the keywords you want to rank for',
      completed: onboarding.keywords_count > 0,
      action: null
    },
    {
      key: 'content',
      title: 'Provide Your Content Preferences',
      description: Object.keys(onboarding.content_preferences || {}).length > 0
        ? 'Content preferences saved'
        : "If you don't have any, that's okay - we'll create content based on best practices",
      completed: Object.keys(onboarding.content_preferences || {}).length > 0,
      action: null
    },
    {
      key: 'access',
      title: 'Provide Access To Your Website',
      description: `Please invite us as an editor, our email is ${onboarding.website_access_email || 'siteaccess@agencyreport.ai'}`,
      completed: onboarding.website_access_granted,
      action: 'View Instructions'
    },
    {
      key: 'gsc',
      title: 'Connect Google Search Console',
      description: 'Connect your Google Search Console to track your search performance',
      completed: onboarding.gsc_connected,
      action: 'Connect Search Console'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card className="bg-card/50 border-white/10">
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold text-white mb-2">
            Welcome, {project.project_name ? project.project_name.split(' ')[0] : 'there'}! Let&apos;s get everything set up.
          </h2>
          <p className="text-gray-400 mb-4">Complete these steps to get started with your {project.service_type?.toUpperCase()} campaign</p>
          
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">Progress</span>
            <Progress value={progress} className="flex-1 h-3" />
            <span className="text-primary font-medium">{progress}% Complete</span>
          </div>
        </CardContent>
      </Card>

      {/* Checklist */}
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Setup Checklist</CardTitle>
          <CardDescription>Complete these tasks to ensure your {project.service_type?.toUpperCase()} campaign runs smoothly</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {checklistItems.map((item, idx) => (
            <div key={item.key} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg">
              <div className={cn(
                "w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0",
                item.completed ? "bg-green-500 border-green-500" : "border-gray-500"
              )}>
                {item.completed && <CheckCircle2 className="w-4 h-4 text-white" />}
              </div>
              <div className="flex-1">
                <h4 className={cn(
                  "font-medium",
                  item.completed ? "text-green-400" : "text-white"
                )}>{item.title}</h4>
                <p className="text-gray-400 text-sm mt-1">{item.description}</p>
                {item.action && !item.completed && (
                  <Button variant="outline" size="sm" className="mt-3" disabled={isDemo}>
                    {item.key === 'access' && <ExternalLink className="w-3 h-3 mr-2" />}
                    {item.action}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== RANKINGS TAB ====================
const RankingsTab = ({ project, isDemo }) => {
  const [subTab, setSubTab] = useState('keywords');
  const rankings = project.rankings || {};
  const keywords = rankings.keywords || [];

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-lg w-fit">
        {['keywords', 'prompts', 'heatmaps'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={cn(
              "px-3 py-1.5 rounded text-sm capitalize",
              subTab === tab ? "bg-white/10 text-white" : "text-gray-400"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {subTab === 'keywords' && (
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-white">Your Tracked Keywords</CardTitle>
                <CardDescription>
                  Last Checked: {rankings.last_checked || 'Never'}<br/>
                  Date Range Selected: {rankings.date_range?.start} - {rankings.date_range?.end}
                </CardDescription>
                <p className="text-gray-300 text-sm mt-2">
                  As a summary, you have {rankings.top_3 || 0} keywords in the top 3, {rankings.top_10 || 0} in the top 10 and {rankings.top_100 || 0} in the top 100. 
                  We&apos;ve seen a net increase of {rankings.net_change || 0} in the time period selected.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={isDemo}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Keywords
                </Button>
                <Select defaultValue="last_30">
                  <SelectTrigger className="w-32">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_7">Last 7 Days</SelectItem>
                    <SelectItem value="last_30">Last 30 Days</SelectItem>
                    <SelectItem value="last_90">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="p-3"><Checkbox disabled={isDemo} /></th>
                  <th className="p-3 text-gray-400 font-medium">Keyword ↑↓</th>
                  <th className="p-3 text-gray-400 font-medium">Desktop ↑↓</th>
                  <th className="p-3 text-gray-400 font-medium">Mobile ↑↓</th>
                  <th className="p-3 text-gray-400 font-medium">Net Change ↓ ✓</th>
                  <th className="p-3 text-gray-400 font-medium">Location ↑↓</th>
                </tr>
              </thead>
              <tbody>
                {keywords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-400">
                      No keywords tracked yet. Add your first keyword to start tracking rankings.
                    </td>
                  </tr>
                ) : (
                  keywords.map(kw => (
                    <tr key={kw.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3"><Checkbox disabled={isDemo} /></td>
                      <td className="p-3 text-cyan-400">{kw.keyword}</td>
                      <td className="p-3 text-white">
                        {kw.desktop_rank ? `${getOrdinal(kw.desktop_rank)}` : '-'}
                        {kw.desktop_change ? <span className="text-green-400 ml-1">(+{kw.desktop_change})</span> : ''}
                      </td>
                      <td className="p-3 text-white">
                        {kw.mobile_rank ? `${getOrdinal(kw.mobile_rank)}` : '-'}
                        {kw.mobile_change ? <span className="text-green-400 ml-1">(+{kw.mobile_change})</span> : ''}
                      </td>
                      <td className="p-3">
                        <span className={cn(
                          "font-bold",
                          (kw.net_change || 0) > 0 ? "text-green-400" : (kw.net_change || 0) < 0 ? "text-red-400" : "text-white"
                        )}>
                          {(kw.net_change || 0) > 0 ? '+' : ''}{kw.net_change || 0}
                        </span>
                      </td>
                      <td className="p-3 text-gray-400">{kw.location || 'United States'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== AUDITS TAB ====================
const AuditsTab = ({ project, isDemo }) => {
  const audits = project.audits || {};
  const issues = audits.issues || [];
  const [filter, setFilter] = useState('all');

  const filteredIssues = issues.filter(issue => {
    if (filter === 'all') return true;
    return issue.severity === filter;
  });

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white">Website Audit Results</CardTitle>
              <CardDescription>
                {issues.length} Total Issues Found<br/>
                {audits.pages_scanned || 0} Pages Scanned<br/>
                {audits.critical_count || 0} Critical • {audits.warning_count || 0} Warning • {audits.notice_count || 0} Notice
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={isDemo}>
                <Play className="w-4 h-4 mr-1" />
                Start Audit
              </Button>
              <Button variant="outline" size="sm" disabled={isDemo}>
                <Clock className="w-4 h-4 mr-1" />
                Audit History
              </Button>
              <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Tabs */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {[
                { id: 'all', label: `All (${issues.length})` },
                { id: 'critical', label: `Critical (${audits.critical_count || 0})` },
                { id: 'warning', label: `Warning (${audits.warning_count || 0})` },
                { id: 'notice', label: `Notice (${audits.notice_count || 0})` },
                { id: 'passed', label: 'Passed (0)' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={cn(
                    "px-3 py-1.5 rounded text-sm",
                    filter === f.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Input placeholder="Filter..." className="w-48" />
          </div>

          {/* Issues Table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="p-3 text-gray-400 font-medium">Issue</th>
                <th className="p-3 text-gray-400 font-medium">Severity</th>
                <th className="p-3 text-gray-400 font-medium">Affected</th>
                <th className="p-3 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-400">
                    No issues found. Run an audit to check your website.
                  </td>
                </tr>
              ) : (
                filteredIssues.map(issue => (
                  <tr key={issue.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                        <div>
                          <p className="text-white font-medium">{issue.title}</p>
                          <p className="text-gray-400 text-sm">{issue.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge className={cn(
                        issue.severity === 'critical' && "bg-red-500/20 text-red-400 border-red-500/30",
                        issue.severity === 'warning' && "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
                        issue.severity === 'notice' && "bg-blue-500/20 text-blue-400 border-blue-500/30"
                      )}>
                        {issue.severity}
                      </Badge>
                    </td>
                    <td className="p-3 text-gray-400">-</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" disabled={isDemo}>
                          <X className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" disabled={isDemo}>
                          <Info className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== BACKLINKS TAB ====================
const BacklinksTab = ({ project, isDemo }) => {
  const backlinks = project.backlinks || {};
  const topDomains = backlinks.top_domains || [];
  const [subTab, setSubTab] = useState('top');

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white">Your Tracked Backlinks</CardTitle>
              <CardDescription>
                Last Checked: {backlinks.last_checked || 'Never'}<br/>
                {backlinks.total?.toLocaleString() || 0} Total Backlinks • {backlinks.referring_domains || 0} Referring Domains
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
              <Button size="sm" disabled={isDemo}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Scan
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sub-tabs */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {[
                { id: 'top', label: `Top Domains (${topDomains.length})` },
                { id: 'new', label: `New (${backlinks.new_count || 0})` },
                { id: 'lost', label: `Lost (${backlinks.lost_count || 0})` }
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setSubTab(t.id)}
                  className={cn(
                    "px-3 py-1.5 rounded text-sm",
                    subTab === t.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <Input placeholder="Filter..." className="w-48" />
          </div>

          {/* Backlinks Table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="p-3 text-gray-400 font-medium">Domain / Backlink URL ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Anchor/Link Quantity ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Domain Rank ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Link Type</th>
                <th className="p-3 text-gray-400 font-medium">First Seen ↑↓</th>
              </tr>
            </thead>
            <tbody>
              {topDomains.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-400">
                    No backlinks tracked yet. Run a scan to discover your backlinks.
                  </td>
                </tr>
              ) : (
                topDomains.map((domain, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{domain.domain}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </td>
                    <td className="p-3 text-gray-400">{domain.links} links</td>
                    <td className="p-3">
                      <span className="text-green-400 font-bold">DR {domain.domain_rank}</span>
                    </td>
                    <td className="p-3 text-gray-400">{domain.link_type}</td>
                    <td className="p-3 text-gray-400">
                      {new Date(domain.first_seen).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== CONTENT TAB ====================
const ContentTab = ({ project, isDemo }) => {
  const content = project.content || {};
  const articles = content.articles || [];
  const [subTab, setSubTab] = useState('blog');
  const [filter, setFilter] = useState('needs_approval');

  const filteredArticles = articles.filter(a => {
    if (subTab === 'blog' && a.type !== 'blog') return false;
    if (subTab === 'web_copy' && a.type !== 'web_copy') return false;
    if (filter !== 'all' && a.status !== filter) return false;
    return true;
  });

  const statusCounts = {
    needs_approval: articles.filter(a => a.status === 'needs_approval').length,
    revising: articles.filter(a => a.status === 'revising').length,
    approved: articles.filter(a => a.status === 'approved').length,
    published: articles.filter(a => a.status === 'published').length
  };

  return (
    <div className="space-y-6">
      {/* Content Type Tabs */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-lg w-fit">
        {['blog', 'web_copy'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={cn(
              "px-3 py-1.5 rounded text-sm",
              subTab === tab ? "bg-white/10 text-white" : "text-gray-400"
            )}
          >
            {tab === 'blog' ? 'Blog Articles' : 'Web Copy'}
          </button>
        ))}
      </div>

      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white">Content</CardTitle>
              <CardDescription>Manage your content for {project.project_name}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={isDemo}>
                <Plus className="w-4 h-4 mr-1" />
                Request Topic
              </Button>
              <Button variant="outline" size="sm" disabled={isDemo}>
                <Settings className="w-4 h-4 mr-1" />
                Content Preferences
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Filter Tabs */}
          <div className="flex gap-2">
            {[
              { id: 'needs_approval', label: `Needs Approval (${statusCounts.needs_approval})` },
              { id: 'revising', label: `Revising (${statusCounts.revising})` },
              { id: 'approved', label: `Approved (${statusCounts.approved})` },
              { id: 'published', label: `Published (${statusCounts.published})` }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "px-3 py-1.5 rounded text-sm",
                  filter === f.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Content Table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="p-3"><Checkbox disabled={isDemo} /></th>
                <th className="p-3 text-gray-400 font-medium">Blog Title ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Date ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Status ↑↓</th>
                <th className="p-3 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredArticles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-400">
                    No content in this category yet.
                  </td>
                </tr>
              ) : (
                filteredArticles.map(article => (
                  <tr key={article.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3"><Checkbox disabled={isDemo} /></td>
                    <td className="p-3 text-white">{article.title}</td>
                    <td className="p-3 text-gray-400">
                      {new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={cn(
                        article.status === 'needs_approval' && "border-amber-500 text-amber-400",
                        article.status === 'revising' && "border-blue-500 text-blue-400",
                        article.status === 'approved' && "border-green-500 text-green-400",
                        article.status === 'published' && "border-cyan-500 text-cyan-400"
                      )}>
                        {article.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {article.status === 'needs_approval' && (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={isDemo}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Approve
                          </Button>
                          <Button variant="outline" size="sm" disabled={isDemo}>
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Revise
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== SEARCH CONSOLE TAB ====================
const SearchConsoleTab = ({ project, isDemo }) => {
  const gsc = project.search_console || {};
  const [showConnectModal, setShowConnectModal] = useState(!gsc.connected && !isDemo);
  const [queryTab, setQueryTab] = useState('queries');

  return (
    <div className="space-y-6">
      {/* Click Performance Chart */}
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Click Performance</CardTitle>
          <CardDescription>
            {gsc.total_clicks?.toLocaleString() || 0} total clicks over time<br/>
            <span className="text-xs">Note: Google Search Console data typically has a 2-3 day delay</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Simple chart visualization */}
          <div className="h-64 relative">
            <div className="absolute inset-0 flex items-end justify-between px-4 pb-8">
              {(gsc.click_performance || []).map((point, idx) => (
                <div key={idx} className="flex flex-col items-center gap-1">
                  <div 
                    className="w-4 bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t"
                    style={{ height: `${(point.clicks / 70) * 100}%`, minHeight: '4px' }}
                  />
                </div>
              ))}
            </div>
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-gray-500 text-xs">
              <span>100</span>
              <span>75</span>
              <span>50</span>
              <span>25</span>
              <span>0</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GSC Data Summary */}
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white">Google Search Console</CardTitle>
              <CardDescription>
                Property: {gsc.property || 'Not connected'} • Date Range: {gsc.date_range?.start} - {gsc.date_range?.end}
              </CardDescription>
              {gsc.connected && (
                <p className="text-gray-300 text-sm mt-2">
                  Search Console shows {gsc.total_impressions?.toLocaleString() || 0} total impressions, {gsc.total_clicks?.toLocaleString() || 0} clicks, with an average CTR of {gsc.avg_ctr || 0}%
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Query Type Tabs */}
          <div className="flex gap-2">
            {[
              { id: 'queries', label: `Search Queries (${gsc.top_queries?.length || 0})` },
              { id: 'pages', label: `Pages (${gsc.pages?.length || 0})` },
              { id: 'countries', label: `Countries (${gsc.countries?.length || 0})` },
              { id: 'devices', label: `Devices (${gsc.devices?.length || 0})` }
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setQueryTab(t.id)}
                className={cn(
                  "px-3 py-1.5 rounded text-sm",
                  queryTab === t.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Queries Table */}
          {queryTab === 'queries' && (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="p-3 text-gray-400 font-medium">Search Query ↑↓</th>
                  <th className="p-3 text-gray-400 font-medium">Impressions ↑</th>
                  <th className="p-3 text-gray-400 font-medium">Clicks ↑</th>
                  <th className="p-3 text-gray-400 font-medium">CTR ↑</th>
                </tr>
              </thead>
              <tbody>
                {(gsc.top_queries || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-400">
                      Connect Google Search Console to see your search queries.
                    </td>
                  </tr>
                ) : (
                  (gsc.top_queries || []).map((query, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3 text-white">{query.query}</td>
                      <td className="p-3 text-gray-400">{query.impressions?.toLocaleString()}</td>
                      <td className="p-3 text-gray-400">{query.clicks}</td>
                      <td className="p-3 text-gray-400">{query.ctr}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Connect Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-card border-white/10 w-96">
            <CardHeader className="text-center">
              <CardTitle className="text-white">Connect Search Console</CardTitle>
              <CardDescription>
                Connect your Google Search Console for us to manage your indexing
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button onClick={() => setShowConnectModal(false)} disabled={isDemo}>
                <Search className="w-4 h-4 mr-2" />
                Connect
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== REPORTS TAB ====================
const ReportsTab = ({ project, isDemo }) => {
  const [config, setConfig] = useState({
    title: `SEO Report - ${project.project_name}`,
    dateRange: 'last_30_days',
    sections: {
      overview: true, keywords: true, ai_prompts: true,
      website_audit: true, backlinks: true, heatmaps: false, search_console: true
    },
    keyword_columns: {
      desktop: true, mobile: true, ai_mode: true, maps: false, net_change: true, location: true
    },
    ai_prompts_columns: {
      prompt: true, volume: true, visibility: true, responses: true, citations: true, models: true
    },
    options: {
      allow_date_changes: true, detailed_tables: true, include_branding: true,
      hide_change_metrics: false, hide_executive_summary: false, hide_highlights: false
    }
  });

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Report Configuration</CardTitle>
          <CardDescription>Configure and generate your SEO report</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Report Title & Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Report Title</Label>
              <Input 
                value={config.title}
                onChange={(e) => setConfig({...config, title: e.target.value})}
                disabled={isDemo}
              />
            </div>
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select 
                value={config.dateRange}
                onValueChange={(v) => setConfig({...config, dateRange: v})}
                disabled={isDemo}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7_days">Last 7 days</SelectItem>
                  <SelectItem value="last_30_days">Last 30 days</SelectItem>
                  <SelectItem value="last_90_days">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Report Sections */}
          <div className="space-y-3">
            <Label className="text-white">Report Sections</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(config.sections).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox 
                    checked={value}
                    onCheckedChange={(v) => setConfig({
                      ...config, 
                      sections: {...config.sections, [key]: v}
                    })}
                    disabled={isDemo}
                  />
                  <span className="text-white capitalize">{key.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Keyword Data Columns */}
          <div className="space-y-3">
            <Label className="text-white">Keyword Data Columns</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(config.keyword_columns).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox 
                    checked={value}
                    onCheckedChange={(v) => setConfig({
                      ...config, 
                      keyword_columns: {...config.keyword_columns, [key]: v}
                    })}
                    disabled={isDemo}
                  />
                  <span className="text-white capitalize">{key.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Prompts Data Columns */}
          <div className="space-y-3">
            <Label className="text-white">AI Prompts Data Columns</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(config.ai_prompts_columns).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox 
                    checked={value}
                    onCheckedChange={(v) => setConfig({
                      ...config, 
                      ai_prompts_columns: {...config.ai_prompts_columns, [key]: v}
                    })}
                    disabled={isDemo}
                  />
                  <span className="text-white capitalize">{key.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Report Options */}
          <div className="space-y-3">
            <Label className="text-white">Report Options</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(config.options).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox 
                    checked={value}
                    onCheckedChange={(v) => setConfig({
                      ...config, 
                      options: {...config.options, [key]: v}
                    })}
                    disabled={isDemo}
                  />
                  <span className="text-white capitalize">{key.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="outline" className="flex-1" disabled={isDemo}>
            <Download className="w-4 h-4 mr-2" />
            Save Configuration
          </Button>
          <Button className="flex-1" disabled={isDemo}>
            <FileText className="w-4 h-4 mr-2" />
            Generate PDF
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

// ==================== SETTINGS TAB ====================
const SettingsTab = ({ project, isDemo }) => {
  const settings = project.settings || project;

  return (
    <div className="space-y-6">
      {/* Settings Sub-tab */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-lg w-fit">
        <button className="px-3 py-1.5 rounded text-sm bg-white/10 text-white">Settings</button>
      </div>

      <Card className="bg-card/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Project Settings</CardTitle>
          <CardDescription>Project configuration {isDemo && '(View Only)'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input value={settings.project_name || project.project_name} disabled />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Input value={settings.status || project.status || 'Active'} disabled className="capitalize" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input value={settings.website_url || project.website_url} disabled />
            </div>
            <div className="space-y-2">
              <Label>Created</Label>
              <Input value={settings.created || new Date(project.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} disabled />
            </div>
          </div>
          
          {isDemo && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
              This is a demo project. All settings are view-only.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== HELPER FUNCTIONS ====================
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// ==================== MAIN PAGE COMPONENT ====================
const DFYProjectsPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(projectId ? 'detail' : 'list');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadProject = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await api.getDFYProject(id);
      setSelectedProject(response.data);
      setView('detail');
      setIsDemo(false);
    } catch (error) {
      toast.error('Project not found');
      navigate('/dfy-projects');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadDemoProject = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getDemoProject();
      setSelectedProject(response.data);
      setView('detail');
      setIsDemo(true);
    } catch (error) {
      toast.error('Failed to load demo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      if (projectId === 'demo') {
        loadDemoProject();
      } else {
        loadProject(projectId);
      }
    }
  }, [projectId, loadProject, loadDemoProject]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setView('detail');
    setIsDemo(false);
    navigate(`/dfy-projects/${project.id}`);
  };

  const handleTakeTour = () => {
    navigate('/dfy-projects/demo');
  };

  const handleCreateProject = () => {
    setView('create');
  };

  const handleProjectCreated = (project) => {
    setSelectedProject(project);
    setView('detail');
    setIsDemo(false);
    navigate(`/dfy-projects/${project.id}`);
  };

  const handleBack = () => {
    setView('list');
    setSelectedProject(null);
    setIsDemo(false);
    navigate('/dfy-projects');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="dfy-projects-page">
        {view === 'list' && (
          <ProjectListView 
            onSelectProject={handleSelectProject}
            onCreateProject={handleCreateProject}
            onTakeTour={handleTakeTour}
          />
        )}
        {view === 'create' && (
          <CreateProjectWizard 
            onBack={handleBack}
            onCreated={handleProjectCreated}
          />
        )}
        {view === 'detail' && selectedProject && (
          <ProjectDetailView 
            project={selectedProject}
            isDemo={isDemo}
            onBack={handleBack}
            onRefresh={() => loadProject(selectedProject.id)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default DFYProjectsPage;
